// packages/web-ui/src/state/changeset.ts
//
// Story W4-S4 AC #9 — `changeset` zustand slice with optimistic updates,
// SSE replay handlers, and dependency-violation rollback.
//
// The slice creator is exported so callers can either compose it into
// `useRootStore` (W4-S4 in-scope file does NOT modify state/store.ts —
// the root-store wiring is owned by DEV 2-2) or instantiate a standalone
// vanilla store via `createStore(createChangesetSlice)` for tests +
// non-shell call-sites.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type {
  KbApplyOutcome,
  KbChangeset,
  KbChangesetProposedPayload,
  KbEntityState,
  KbEntityStatus,
} from '@tessa-lean/base';

import { changesetApi as defaultChangesetApi, type ChangesetApi, DEPENDENCY_VIOLATION_CODE } from '../api/clients/changeset.js';
import type { ApiContext } from '../api/client.js';

/**
 * Stack entry for the available-undo ring buffer maintained by the SPA. One
 * entry is pushed every time `kb:changesetApplied` arrives with
 * `changesetComplete: true`; the entry is removed when the matching
 * `kb:changesetUndone` SSE event lands. Capped at {@link UNDO_STACK_CAP} to
 * mirror the server's ring-buffer depth.
 */
export interface AvailableUndoChangeset {
  changesetId: string;
  /** ISO 8601 timestamp captured when the apply completed on the SPA. */
  acceptedAt: string;
  /** Number of files written by the applier (derived from outcomes). */
  fileCount: number;
}

/** Hard cap on the available-undo stack. Mirrors the server ring-buffer. */
export const UNDO_STACK_CAP = 5;

/**
 * Transient "undo in-flight" markers. Keyed by changesetId; populated
 * by `dispatchUndoLastChangeset` and cleared either by the matching
 * `kb:changesetUndone` SSE event or by a transient error notice. The
 * `errorMessage` field is set when the dispatch failed and surfaces an
 * inline 5-second error message in the `<KbChangeTree>` footer.
 */
export interface UndoInFlight {
  changesetId: string;
  /** Wall-clock when the dispatch was sent — used to drive the ~5 s auto-clear. */
  dispatchedAt: number;
  /** Optional error surfaced after a failed dispatch. */
  errorMessage?: string;
}

/** State fields owned by this slice. */
export interface ChangesetSliceState {
  /** Active changesets indexed by id. Loaded lazily on first inline render. */
  changesetsById: Record<string, KbChangeset>;
  /** Per-entity state arrays indexed by changesetId. */
  entityStatesByChangesetId: Record<string, KbEntityState[]>;
  /**
   * Server-emitted dependency-violation messages keyed by entityId.
   * Populated when `acceptEntity` returns 409 DEPENDENCY_VIOLATION; the
   * `<KbChangeTree>` component reads this map and merges with its local
   * client-side `dependencyWarnings` state (parity with Lit's
   * `_dependencyWarnings` line 124).
   */
  dependencyWarningsByEntityId: Record<string, string>;
  /** History of past changesets — populated by Diff tab in W4-S7; this story leaves it []. */
  changesetHistory: string[];
  /**
   * KB-undo ring buffer. Most-recent first. Each entry corresponds to a
   * fully applied changeset (`kb:changesetApplied` with
   * `changesetComplete: true`); capped at {@link UNDO_STACK_CAP} (oldest
   * evicted on overflow, matching the server ring-buffer size).
   */
  availableUndoChangesets: AvailableUndoChangeset[];
  /**
   * Per-changeset in-flight undo state. `null` (or absent) means the
   * dispatched undo has not been sent. The dispatcher sets a marker
   * before the HTTP POST and clears it when the SSE round-trip arrives
   * (or on dispatch error after the inline-error timeout).
   */
  undoInFlightById: Record<string, UndoInFlight>;
}

export const CHANGESET_INITIAL_STATE: ChangesetSliceState = {
  changesetsById: {},
  entityStatesByChangesetId: {},
  dependencyWarningsByEntityId: {},
  changesetHistory: [],
  availableUndoChangesets: [],
  undoInFlightById: {},
};

/** Action surface owned by this slice. */
export interface ChangesetSliceActions {
  /** Load changeset + entityStates from the server. Idempotent on changesetId. */
  loadChangeset: (changesetId: string) => Promise<void>;

  /** Optimistic accept; rolls back the local status flip on 409 DEPENDENCY_VIOLATION. */
  acceptEntity: (changesetId: string, entityId: string) => Promise<void>;
  /** Optimistic reject. */
  rejectEntity: (changesetId: string, entityId: string) => Promise<void>;
  /** Local-only — `pending` is the no-op state, no server call. */
  setEntityPending: (changesetId: string, entityId: string) => void;

  /** Cycle helper: dispatches the right action per `nextStatus`. */
  cycleEntity: (changesetId: string, entityId: string, nextStatus: KbEntityStatus) => Promise<void>;

  /** `POST .../accept-all`. Optimistic-marks every non-terminal entity. */
  acceptAll: (changesetId: string) => Promise<void>;
  /** `POST .../reject-all`. Optimistic-marks every non-terminal entity. */
  rejectAll: (changesetId: string) => Promise<void>;

  /** Retry a failed entity via re-POST accept (FR-DIFF-5). */
  retryEntity: (changesetId: string, entityId: string) => Promise<void>;

  /** SSE handler — applies a `kb:changesetProposed` payload verbatim. */
  applyChangesetProposed: (payload: KbChangesetProposedPayload) => void;
  /** SSE handler — server confirmed an accept (no downgrade from `applied`). */
  applyEntityAccepted: (changesetId: string, entityId: string) => void;
  /** SSE handler — server confirmed a reject. */
  applyEntityRejected: (changesetId: string, entityId: string) => void;
  /** SSE handler — applier wrote the file(s) (or failed). */
  applyChangesetApplied: (
    changesetId: string,
    outcomes: KbApplyOutcome[],
    options?: { changesetComplete?: boolean },
  ) => void;

  /**
   * KB-undo SSE handler — remove the changeset from the available-undo
   * stack and clear any in-flight marker. Called by `attach-to-chat-slice`
   * when a `kb:changesetUndone` SSE event arrives.
   */
  applyChangesetUndone: (changesetId: string) => void;

  /**
   * KB-undo error handler — set/clear an in-flight error message so the
   * `<KbChangeTree>` footer can render an inline 5-second error notice.
   * `errorMessage === null` clears the marker (used by the SPA's auto-
   * clear timer).
   */
  setUndoError: (changesetId: string, errorMessage: string | null) => void;

  /**
   * KB-undo dispatcher — POST `/api/v1/changeset/:id/undo` for the
   * most-recent changeset on the available-undo stack (or the specific
   * id when provided). Sets a transient in-flight marker; the slice
   * clears it when `applyChangesetUndone` arrives via SSE.
   * Returns the changesetId that was dispatched, or `null` when the
   * stack is empty.
   */
  dispatchUndoLastChangeset: (changesetId?: string) => Promise<string | null>;

  /** SSE-reconnect: full state refetch (Arch §10.2 line 2207). */
  refetchOnReconnect: (changesetId: string) => Promise<void>;

  /**
   * Test/debug helper — reset the slice. Per-slice prefixed (MED-2) so the
   * RootStore composition no longer collides on a shared `_reset` name.
   */
  _resetChangeset: () => void;
}

export type ChangesetSlice = ChangesetSliceState & ChangesetSliceActions;

/** Override hooks for the slice — used by tests to inject an api stub. */
export interface ChangesetSliceConfig {
  api?: ChangesetApi;
  apiContext?: ApiContext;
}

const TERMINAL: ReadonlySet<KbEntityStatus> = new Set<KbEntityStatus>([
  'applied',
  'rejected',
  'failed',
  'unresolvable',
]);

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('tessaLeanToken') : null;
  return { baseUrl: '', token };
}

/** Replace one entity's status, append if absent (parity with Lit's upsertEntityStatus). */
function upsertEntityStatus(
  states: KbEntityState[],
  entityId: string,
  status: KbEntityStatus,
  failureMessage?: string,
): KbEntityState[] {
  const next = states.map((s) => ({ ...s }));
  const idx = next.findIndex((s) => s.entityId === entityId);
  const row: KbEntityState =
    status === 'failed' && failureMessage
      ? { entityId, status, failureMessage }
      : { entityId, status };
  if (idx === -1) next.push(row);
  else next[idx] = row;
  return next;
}

/**
 * zustand StateCreator — composes into a root store via the slice
 * pattern, OR can be wrapped in a vanilla store via {@link createChangesetStore}
 * for standalone use.
 */
export const createChangesetSlice =
  (config: ChangesetSliceConfig = {}): StateCreator<ChangesetSlice> =>
  (set, get) => {
    const api = config.api ?? defaultChangesetApi;
    const ctx = config.apiContext ?? defaultApiContext();

    /** Helper — apply an entity-status mutation to the slice. */
    const mutateStates = (
      changesetId: string,
      mutator: (states: KbEntityState[]) => KbEntityState[],
    ): void => {
      set((s) => {
        const prev = s.entityStatesByChangesetId[changesetId];
        if (!prev) return s;
        return {
          ...s,
          entityStatesByChangesetId: {
            ...s.entityStatesByChangesetId,
            [changesetId]: mutator(prev),
          },
        };
      });
    };

    return {
      ...CHANGESET_INITIAL_STATE,

      loadChangeset: async (changesetId) => {
        if (get().changesetsById[changesetId]) return;
        const res = await api.getChangeset(ctx, changesetId);
        if (res.ok) {
          set((s) => ({
            ...s,
            changesetsById: { ...s.changesetsById, [changesetId]: res.value.changeset },
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: res.value.entityStates,
            },
          }));
        }
      },

      acceptEntity: async (changesetId, entityId) => {
        const prev = get().entityStatesByChangesetId[changesetId];
        if (!prev) return;
        // Optimistic
        mutateStates(changesetId, (states) =>
          upsertEntityStatus(states, entityId, 'accepted'),
        );
        const res = await api.acceptEntity(ctx, changesetId, entityId);
        if (!res.ok) {
          // Rollback
          set((s) => ({
            ...s,
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: prev,
            },
          }));
          if (res.error.code === DEPENDENCY_VIOLATION_CODE) {
            set((s) => ({
              ...s,
              dependencyWarningsByEntityId: {
                ...s.dependencyWarningsByEntityId,
                [entityId]: res.error.message,
              },
            }));
          }
        } else {
          // Clear any stale dependency warning on success
          set((s) => {
            if (!(entityId in s.dependencyWarningsByEntityId)) return s;
            const next = { ...s.dependencyWarningsByEntityId };
            delete next[entityId];
            return { ...s, dependencyWarningsByEntityId: next };
          });
        }
      },

      rejectEntity: async (changesetId, entityId) => {
        const prev = get().entityStatesByChangesetId[changesetId];
        if (!prev) return;
        mutateStates(changesetId, (states) =>
          upsertEntityStatus(states, entityId, 'rejected'),
        );
        const res = await api.rejectEntity(ctx, changesetId, entityId);
        if (!res.ok) {
          set((s) => ({
            ...s,
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: prev,
            },
          }));
        }
      },

      setEntityPending: (changesetId, entityId) => {
        mutateStates(changesetId, (states) =>
          upsertEntityStatus(states, entityId, 'pending'),
        );
      },

      cycleEntity: async (changesetId, entityId, nextStatus) => {
        if (nextStatus === 'accepted') {
          await get().acceptEntity(changesetId, entityId);
        } else if (nextStatus === 'rejected') {
          await get().rejectEntity(changesetId, entityId);
        } else if (nextStatus === 'pending') {
          get().setEntityPending(changesetId, entityId);
        }
      },

      acceptAll: async (changesetId) => {
        const prev = get().entityStatesByChangesetId[changesetId];
        if (!prev) return;
        // Optimistic — mark every non-terminal entity accepted
        mutateStates(changesetId, (states) =>
          states.map((s) =>
            TERMINAL.has(s.status) ? s : { ...s, status: 'accepted' as KbEntityStatus },
          ),
        );
        const res = await api.acceptAll(ctx, changesetId);
        if (!res.ok) {
          set((s) => ({
            ...s,
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: prev,
            },
          }));
        }
      },

      rejectAll: async (changesetId) => {
        const prev = get().entityStatesByChangesetId[changesetId];
        if (!prev) return;
        mutateStates(changesetId, (states) =>
          states.map((s) =>
            s.status === 'applied' || s.status === 'rejected'
              ? s
              : { ...s, status: 'rejected' as KbEntityStatus },
          ),
        );
        const res = await api.rejectAll(ctx, changesetId);
        if (!res.ok) {
          set((s) => ({
            ...s,
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: prev,
            },
          }));
        }
      },

      retryEntity: async (changesetId, entityId) => {
        // Re-arm to pending, then re-accept.
        mutateStates(changesetId, (states) =>
          upsertEntityStatus(states, entityId, 'pending'),
        );
        await get().acceptEntity(changesetId, entityId);
      },

      applyChangesetProposed: (payload) => {
        set((s) => ({
          ...s,
          changesetsById: { ...s.changesetsById, [payload.changeset.id]: payload.changeset },
          entityStatesByChangesetId: {
            ...s.entityStatesByChangesetId,
            [payload.changeset.id]: payload.entityStates,
          },
        }));
      },

      applyEntityAccepted: (changesetId, entityId) => {
        const states = get().entityStatesByChangesetId[changesetId];
        if (!states) return;
        const current = states.find((e) => e.entityId === entityId);
        // Idempotency — never downgrade applied → accepted.
        if (current?.status === 'applied') return;
        mutateStates(changesetId, (s) =>
          upsertEntityStatus(s, entityId, 'accepted'),
        );
      },

      applyEntityRejected: (changesetId, entityId) => {
        const states = get().entityStatesByChangesetId[changesetId];
        if (!states) return;
        const current = states.find((e) => e.entityId === entityId);
        // Idempotency — never overwrite a terminal applied with rejected.
        if (current?.status === 'applied') return;
        mutateStates(changesetId, (s) =>
          upsertEntityStatus(s, entityId, 'rejected'),
        );
      },

      applyChangesetApplied: (changesetId, outcomes, options) => {
        const states = get().entityStatesByChangesetId[changesetId];
        if (!states) return;
        let next = states;
        for (const outcome of outcomes) {
          const current = next.find((e) => e.entityId === outcome.entityId);
          // Idempotency — skip if already in the same outcome bucket.
          if (current?.status === outcome.status) continue;
          if (outcome.status === 'failed') {
            next = upsertEntityStatus(
              next,
              outcome.entityId,
              'failed',
              outcome.error?.message,
            );
          } else {
            next = upsertEntityStatus(next, outcome.entityId, outcome.status);
          }
        }
        const changesetComplete = options?.changesetComplete === true;
        set((s) => {
          const base = {
            ...s,
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: next,
            },
          };
          // KB-undo: push onto the available-undo stack only on the
          // terminal applied emission. Idempotent on changesetId
          // (replay-safe: a duplicate complete emit moves the entry to
          // the front but never doubles the count).
          if (!changesetComplete) return base;
          const fileCount = outcomes.filter((o) => o.status === 'applied').length;
          if (fileCount === 0) return base; // nothing actually written
          const existingIdx = s.availableUndoChangesets.findIndex(
            (e) => e.changesetId === changesetId,
          );
          const entry: AvailableUndoChangeset = {
            changesetId,
            acceptedAt: new Date().toISOString(),
            fileCount,
          };
          const remaining =
            existingIdx >= 0
              ? s.availableUndoChangesets.filter((_, i) => i !== existingIdx)
              : s.availableUndoChangesets;
          const pushed = [entry, ...remaining];
          const trimmed =
            pushed.length > UNDO_STACK_CAP ? pushed.slice(0, UNDO_STACK_CAP) : pushed;
          return { ...base, availableUndoChangesets: trimmed };
        });
      },

      applyChangesetUndone: (changesetId) => {
        set((s) => {
          const filtered = s.availableUndoChangesets.filter(
            (e) => e.changesetId !== changesetId,
          );
          const inFlightCopy = { ...s.undoInFlightById };
          delete inFlightCopy[changesetId];
          return {
            ...s,
            availableUndoChangesets: filtered,
            undoInFlightById: inFlightCopy,
          };
        });
      },

      setUndoError: (changesetId, errorMessage) => {
        set((s) => {
          const existing = s.undoInFlightById[changesetId];
          const inFlightCopy = { ...s.undoInFlightById };
          if (errorMessage === null) {
            delete inFlightCopy[changesetId];
          } else {
            inFlightCopy[changesetId] = {
              changesetId,
              dispatchedAt: existing?.dispatchedAt ?? Date.now(),
              errorMessage,
            };
          }
          return { ...s, undoInFlightById: inFlightCopy };
        });
      },

      dispatchUndoLastChangeset: async (changesetIdArg) => {
        const top = get().availableUndoChangesets[0];
        const targetId = changesetIdArg ?? top?.changesetId ?? null;
        if (targetId === null) return null;
        // Mark in-flight before the network round-trip; the SSE handler
        // clears it on success, the error path clears it after the
        // 5-second inline-error TTL (handled by the component).
        set((s) => ({
          ...s,
          undoInFlightById: {
            ...s.undoInFlightById,
            [targetId]: { changesetId: targetId, dispatchedAt: Date.now() },
          },
        }));
        const res = await api.undoChangeset(ctx, targetId);
        if (!res.ok) {
          // Surface the error inline; component renders for ~5s.
          set((s) => ({
            ...s,
            undoInFlightById: {
              ...s.undoInFlightById,
              [targetId]: {
                changesetId: targetId,
                dispatchedAt: Date.now(),
                errorMessage: res.error.message,
              },
            },
          }));
        }
        return targetId;
      },

      refetchOnReconnect: async (changesetId) => {
        const res = await api.getChangeset(ctx, changesetId);
        if (res.ok) {
          set((s) => ({
            ...s,
            changesetsById: { ...s.changesetsById, [changesetId]: res.value.changeset },
            entityStatesByChangesetId: {
              ...s.entityStatesByChangesetId,
              [changesetId]: res.value.entityStates,
            },
          }));
        }
      },

      _resetChangeset: () => {
        set({ ...CHANGESET_INITIAL_STATE } as ChangesetSlice);
      },
    };
  };

/** Vanilla-store factory for standalone use (tests, non-shell call-sites). */
export function createChangesetStore(config: ChangesetSliceConfig = {}) {
  return createStore<ChangesetSlice>()(createChangesetSlice(config));
}

/**
 * KB-undo selector — `true` when at least one applied changeset remains in
 * the available-undo ring buffer. Components subscribe to this via the
 * standard zustand `useStore(store, selectCanUndo)` pattern.
 */
export function selectCanUndo(state: ChangesetSlice): boolean {
  return state.availableUndoChangesets.length > 0;
}
